/**
 * Tela de pareamento do painel TV.
 * 1. Chama /api/tv/pair/init → recebe código de 6 chars
 * 2. Faz polling em /api/tv/pair/poll/{code} a cada 3s
 * 3. Quando approved → chama onApproved(token)
 * 4. Quando expired → reinicia automaticamente
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { PiCheckBold, PiCopyBold } from 'react-icons/pi';

const publicApi = axios.create({
    baseURL: '/api/tv',
    headers: { Accept: 'application/json' },
});

type Phase = 'loading' | 'waiting' | 'approved' | 'error' | 'rate_limited';

type Props = {
    onApproved: (token: string) => void;
};

export default function TvPairingScreen({ onApproved }: Props) {
    const [phase, setPhase]         = useState<Phase>('loading');
    const [code, setCode]           = useState<string | null>(null);
    const [expiresIn, setExpiresIn] = useState(0);
    const [dots, setDots]           = useState('');
    const [copied, setCopied]       = useState(false);
    const pollRef  = useRef<ReturnType<typeof setInterval> | null>(null);
    const countRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopIntervals = () => {
        if (pollRef.current)  clearInterval(pollRef.current);
        if (countRef.current) clearInterval(countRef.current);
    };

    const startPairing = useCallback(async () => {
        stopIntervals();
        setPhase('loading');
        setCode(null);

        try {
            const { data } = await publicApi.post('/pair/init');
            setCode(data.code);
            setExpiresIn(data.expires_in);
            setPhase('waiting');

            countRef.current = setInterval(() => {
                setExpiresIn((prev) => Math.max(0, prev - 1));
            }, 1000);

            pollRef.current = setInterval(async () => {
                try {
                    const { data: poll } = await publicApi.get(`/pair/poll/${data.code}`);
                    if (poll.status === 'approved') {
                        stopIntervals();
                        setPhase('approved');
                        onApproved(poll.token);
                    } else if (poll.status === 'expired') {
                        stopIntervals();
                        startPairing();
                    }
                } catch {
                    // ignora erros de rede temporários
                }
            }, 3000);
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 429) {
                setPhase('rate_limited');
                // tenta novamente após 60s
                setTimeout(() => startPairing(), 60_000);
            } else {
                setPhase('error');
            }
        }
    }, [onApproved]);

    useEffect(() => {
        const id = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), 600);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        startPairing();
        return stopIntervals;
    }, []);

    const mins = Math.floor(expiresIn / 60);
    const secs = String(expiresIn % 60).padStart(2, '0');

    return (
        <div className="relative flex min-h-screen flex-col items-center justify-center bg-zinc-950 px-6 text-white overflow-hidden">
            {/* Faixa dourada no topo */}
            <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{ background: 'linear-gradient(90deg, #3D2D08 0%, #8B6914 20%, #C9A84C 50%, #F0D06A 65%, #C9A84C 80%, #8B6914 100%)' }}
            />

            {/* Brilho de fundo decorativo */}
            <div
                className="pointer-events-none absolute inset-0 opacity-10"
                style={{
                    background: 'radial-gradient(ellipse 60% 40% at 50% 0%, #C9A84C 0%, transparent 70%)',
                }}
            />

            {/* Logo + branding */}
            <div className="mb-12 flex flex-col items-center text-center">
                <img
                    src="/img/design/Logo_Buckler_OffWhite.png"
                    alt="Buckler Fit"
                    className="mb-5 h-16 object-contain drop-shadow-xl"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <p
                    className="text-[11px] font-bold uppercase tracking-[0.3em]"
                    style={{ color: '#C9A84C' }}
                >
                    Painel Financeiro · Modo TV
                </p>
                <h1 className="mt-2 text-5xl font-black tracking-tight text-white">
                    Pareamento necessário
                </h1>
                <p className="mt-2 text-base text-gray-400">
                    Autorize esta TV no painel administrativo para continuar
                </p>
            </div>

            {/* ── Estados ── */}
            {phase === 'loading' && (
                <div className="flex items-center gap-3 text-xl text-gray-400">
                    <span
                        className="inline-block size-5 animate-spin rounded-full border-2 border-[#C9A84C]/30 border-t-[#C9A84C]"
                    />
                    Gerando código{dots}
                </div>
            )}

            {phase === 'error' && (
                <div className="text-center">
                    <p className="text-xl text-red-400">Erro ao gerar código.</p>
                    <p className="mt-1 text-sm text-gray-500">Verifique a conexão com o servidor.</p>
                    <button
                        type="button"
                        onClick={startPairing}
                        className="btn-gold mt-4 rounded-xl px-6 py-2.5 text-sm"
                    >
                        Tentar novamente
                    </button>
                </div>
            )}

            {phase === 'rate_limited' && (
                <div className="text-center">
                    <div className="mb-4 text-4xl">⏳</div>
                    <p className="text-xl font-bold" style={{ color: '#C9A84C' }}>
                        Muitas tentativas
                    </p>
                    <p className="mt-2 text-sm text-gray-400">
                        Aguarde um momento — nova tentativa automática em 60s{dots}
                    </p>
                </div>
            )}

            {phase === 'waiting' && code && (
                <div className="w-full max-w-sm text-center">
                    {/* Caixa do código */}
                    <div
                        className="rounded-3xl px-8 py-10 shadow-2xl"
                        style={{
                            background: 'linear-gradient(160deg, #1a1209 0%, #2a1f0a 100%)',
                            border: '1.5px solid',
                            borderColor: '#C9A84C',
                            boxShadow: '0 0 40px rgba(201,168,76,0.15), 0 8px 32px rgba(0,0,0,0.5)',
                        }}
                    >
                        <p
                            className="text-sm font-semibold uppercase tracking-widest"
                            style={{ color: '#C9A84C' }}
                        >
                            Código de pareamento
                        </p>

                        {/* Código com letras separadas */}
                        <div className="mt-5 flex justify-center gap-2">
                            {code.split('').map((char, i) => (
                                <span
                                    key={i}
                                    className="flex size-14 items-center justify-center rounded-xl font-mono text-3xl font-black text-white"
                                    style={{
                                        background: 'rgba(201,168,76,0.08)',
                                        border: '1px solid rgba(201,168,76,0.25)',
                                    }}
                                >
                                    {char}
                                </span>
                            ))}
                        </div>

                        <p className="mt-5 text-xs text-gray-600">
                            Expira em{' '}
                            <span
                                className="font-mono font-bold"
                                style={{ color: expiresIn < 60 ? '#ef4444' : '#C9A84C' }}
                            >
                                {mins}:{secs}
                            </span>
                        </p>

                        {/* Botão copiar */}
                        <button
                            type="button"
                            onClick={() => {
                                if (!code) return;
                                navigator.clipboard.writeText(code).then(() => {
                                    setCopied(true);
                                    setTimeout(() => setCopied(false), 2000);
                                });
                            }}
                            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all"
                            style={copied ? {
                                background: 'rgba(16,185,129,0.15)',
                                border: '1px solid rgba(16,185,129,0.4)',
                                color: '#34d399',
                            } : {
                                background: 'rgba(201,168,76,0.10)',
                                border: '1px solid rgba(201,168,76,0.30)',
                                color: '#C9A84C',
                            }}
                        >
                            {copied
                                ? <><PiCheckBold className="size-4" /> Copiado!</>
                                : <><PiCopyBold className="size-4" /> Copiar código</>
                            }
                        </button>
                    </div>

                    {/* Instruções */}
                    <div className="mt-8 space-y-4 text-left">
                        <Step n={1} text="No computador, acesse o painel administrativo" />
                        <Step n={2} text={<>Clique em <strong className="text-[#F0D06A]">Parear TV</strong> no cabeçalho superior</>} />
                        <Step n={3} text="Digite o código acima para autorizar esta tela" />
                    </div>

                    <p className="mt-8 animate-pulse text-sm text-gray-500">
                        Aguardando autorização{dots}
                    </p>
                </div>
            )}

            {phase === 'approved' && (
                <div className="text-center">
                    <div
                        className="mx-auto flex size-20 items-center justify-center rounded-full text-4xl shadow-lg"
                        style={{
                            background: 'linear-gradient(135deg, #8B6914 0%, #C9A84C 50%, #F0D06A 100%)',
                            boxShadow: '0 0 30px rgba(201,168,76,0.4)',
                        }}
                    >
                        ✓
                    </div>
                    <p className="mt-4 text-2xl font-black" style={{ color: '#F0D06A' }}>
                        TV autorizada!
                    </p>
                    <p className="mt-2 text-sm text-gray-400">Carregando painel{dots}</p>
                </div>
            )}

            {/* URL de ajuda */}
            <p className="absolute bottom-6 text-xs text-zinc-700">
                {window.location.origin}/tv/finance
            </p>
        </div>
    );
}

function Step({ n, text }: { n: number; text: React.ReactNode }) {
    return (
        <div className="flex items-start gap-3">
            <span
                className="flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-black text-zinc-900"
                style={{
                    background: 'linear-gradient(135deg, #8B6914 0%, #C9A84C 100%)',
                }}
            >
                {n}
            </span>
            <p className="pt-0.5 text-sm text-gray-400">{text}</p>
        </div>
    );
}
